import { LifeCycles, registerApplication, start } from "single-spa";
import { match, MatchFunction } from "path-to-regexp";
import { Constants } from "../constants/constants";

declare var System: any;
/**
 * Enum for supported activeWhen functions.
 */
export enum ActiveWhenFunction {
  Exact = "exact",
  StartsWith = "startswith",
  EndsWith = "endswith",
  Contains = "contains",
}

/**
 * ActiveWhen condition structure
 */
export interface ActiveWhenCondition {
  path: string;
  function: ActiveWhenFunction;
}

/**
 * Resource configuration
 */
export interface ResourceConfig {
  entryPoint: boolean;
  useImportMap: boolean;
  key?: string;
  url: string;
}

/**
 * Module configuration from registry
 */
export interface ModuleConfiguration<ExtraProps = Record<string, any>> {
  name: string;
  activeWhen: ActiveWhenCondition[];
  resources: ResourceConfig[];
  props: ExtraProps;

  // Always mounted apps (e.g., navbar)
  alwaysActive?: boolean;
}

/**
 * Single-SPA Manager
 */
export class SingleSpaManager {
  private configurations: ModuleConfiguration[] = [];

  // Cache compiled matchers
  private matcherCache = new Map<
    string,
    MatchFunction<Record<string, string>>
  >();

  /**
   * Entry point
   */
  async loadSingleSpa(registryUrl: string): Promise<void> {
    try {
      console.log("[SingleSpaManager] Initializing...");

      await this.loadConfigurations(registryUrl);

      this.registerImportMaps();
      this.registerApplications();
      this.start();

      console.log("[SingleSpaManager] Started successfully.");
    } catch (error) {
      console.error("[SingleSpaManager] Failed:", error);
      throw error;
    }
  }

  /**
   * Load configurations from registry
   */
  private async loadConfigurations(registryUrl: string): Promise<void> {
    const response = await fetch(registryUrl, {
      method: "GET",
      headers: { "x-csrf": "1" },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch configurations: ${response.statusText}`);
    }

    this.configurations = await response.json();
  }

  /**
   * Get cached matcher
   */
  private getMatcher(
    path: string,
    end: boolean
  ): MatchFunction<Record<string, string>> {
    const key = `${path}_${end}`;

    if (!this.matcherCache.has(key)) {
      const matcher = match<Record<string, string>>(path, { end });
      this.matcherCache.set(key, matcher);
    }

    return this.matcherCache.get(key)!;
  }

  /**
   * Convert activeWhen conditions → executable functions
   */
  private parseActiveWhen(
    conditions: ActiveWhenCondition[]
  ): ((location: Location) => boolean)[] {
    return conditions.map((condition) => {
      switch (condition.function) {
        case ActiveWhenFunction.Exact: {
          const matcher = this.getMatcher(condition.path, true);
          return (location: Location) => !!matcher(location.pathname);
        }

        case ActiveWhenFunction.StartsWith: {
          return (location: Location) =>
            location.pathname.startsWith(condition.path);
        }

        case ActiveWhenFunction.EndsWith:
          return (location: Location) =>
            location.pathname.endsWith(condition.path);

        case ActiveWhenFunction.Contains:
          return (location: Location) =>
            location.pathname.includes(condition.path);

        default:
          throw new Error(
            `Unsupported activeWhen function: ${condition.function}`
          );
      }
    });
  }

  /**
   * Process module resources
   */
  private processResources(resources: ResourceConfig[]): {
    entryPoint: string;
    importMap: Record<string, string>;
    dynamicScripts: string[];
  } {
    const importMap: Record<string, string> = {};
    const dynamicScripts: string[] = [];
    let entryPointUrl: string | null = null;

    resources.forEach((resource) => {
      if (resource.entryPoint) {
        entryPointUrl = resource.url;
      }

      if (resource.useImportMap) {
        if (!resource.key) {
          throw new Error("Import map resource must have a key.");
        }
        importMap[resource.key] = resource.url;
      } else {
        dynamicScripts.push(resource.url);
      }
    });

    if (!entryPointUrl) {
      throw new Error("No entry point defined.");
    }

    return {
      entryPoint: entryPointUrl,
      importMap,
      dynamicScripts,
    };
  }

  /**
   * Register global import maps
   */
  private registerImportMaps(): void {
    const importMap: Record<string, string> = {};

    this.configurations.forEach((module) => {
      module.resources.forEach((resource) => {
        if (resource.useImportMap && resource.key) {
          importMap[resource.key] = resource.url;
        }
      });
    });

    if (Object.keys(importMap).length > 0) {
      System.addImportMap({ imports: importMap });
      console.log("[SingleSpaManager] Import maps registered.");
    }
  }

  /**
   * Register all applications
   */
  private registerApplications(): void {
    this.configurations.forEach((module) => {
      const activeFns = this.parseActiveWhen(module.activeWhen);

      const { entryPoint, dynamicScripts } = this.processResources(
        module.resources
      );

      registerApplication({
        name: module.name,

        app: async () => {
          const appModule = await System.import(entryPoint);

          await Promise.all(
            dynamicScripts.map((script) => System.import(script))
          );

          return appModule;
        },

        activeWhen: (location) => {
          if (module.alwaysActive) return true;

          return activeFns.some((fn) => fn(location));
        },

        customProps: module.props,
      });

      console.log(`[SingleSpaManager] Registered: ${module.name}`);
    });
    this.registerUnauthorizedApplication();
    this.registerNotFoundApplication();
  }

  private registerNotFoundApplication(): void {
    registerApplication({
      name: Constants.ImportMapKeys.NOT_FOUND,

      app: () => System.import(Constants.ImportMapKeys.NOT_FOUND),

      activeWhen: (location) => {
        const isAnyModuleActive = this.configurations.some((module) => {
          if (module.alwaysActive) return false;

          const activeFns = this.parseActiveWhen(module.activeWhen);

          return activeFns.some((fn) => fn(location));
        });

        return (
          [Constants.Routes.HOME, Constants.Routes.UNAUTHORIZED].every(
            (path) => location.pathname !== path
          ) && !isAnyModuleActive
        );
      },

      customProps: {},
    });

    console.log("[SingleSpaManager] Registered: not-found");
  }

  private registerUnauthorizedApplication(): void {
    registerApplication({
      name: Constants.ImportMapKeys.UNAUTHORIZED,

      app: () => System.import(Constants.ImportMapKeys.UNAUTHORIZED),

      activeWhen: (location) => {
        return location.pathname == Constants.Routes.UNAUTHORIZED;
      },

      customProps: {},
    });

    console.log("[SingleSpaManager] Registered: unauthorized");
  }

  /**
   * Start Single-SPA
   */
  private start(): void {
    start({ urlRerouteOnly: true });
  }
}
